import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSpring, animated, config } from 'react-spring';
import delay from "delay";
import './App.scss';


function hslToRgb(h, s, l) {
  const H = h % 360;
  const S = s / 100;
  const L = l / 100;

  const C = (1 - Math.abs(2 * L - 1)) * S;
  const X = C * (1 - Math.abs((H / 60) % 2 - 1));
  const m = L - C / 2;
  let r_, g_, b_;
  if (0 <= H && H < 60) [r_, g_, b_] = [C, X, 0];
  if (60 <= H && H < 120) [r_, g_, b_] = [X, C, 0];
  if (120 <= H && H < 180) [r_, g_, b_] = [0, C, X];
  if (180 <= H && H < 240) [r_, g_, b_] = [0, X, C];
  if (240 <= H && H < 300) [r_, g_, b_] = [X, 0, C];
  if (300 <= H && H < 360) [r_, g_, b_] = [C, 0, X];

  return {
    R: Math.floor((r_ + m) * 255 + 0.5),
    G: Math.floor((g_ + m) * 255 + 0.5),
    B: Math.floor((b_ + m) * 255 + 0.5)
  };
}

function randomConfig() {
  const configs = [config.default, config.gentle, config.molasses, config.slow, config.stiff, config.wobbly];
  const idx = Math.floor(Math.random() * configs.length);
  return configs[idx];
}

function handleFiles(files) {
  const images = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (file.type.startsWith('image/')) {
      images.push(file);
    }
  }
  return images;
}

function getRandomInt(min, max) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min) + min); //The maximum is exclusive and the minimum is inclusive
}

/** aを内部でシャッフルする */
function doFisherYatesShuffle(a) {
  if (a == null || !Array.isArray(a) || a.length < 2) {
    return;
  }
  const n = a.length;
  for (let i = n - 1; i > 0; i--) {
    let j = getRandomInt(0, i + 1);
    [a[j], a[i]] = [a[i], a[j]];
  }
}


function App() {
  const appRef = useRef(null);
  const [full, setIsFull] = useState(false);
  const [images, setImages] = useState([]);
  const [imageSrc, setImageSrc] = useState(null);
  const [enableAutoScroll, setAutoScroll] = useState(true);
  const [isFitImage, setFitImage] = useState(true);
  // 1を左上として電卓風に並ぶ1~9の数値
  const [imagePosition, setImagePosition] = useState(5);
  const [imgOpacity, setImgOpacity] = useState(0.6);
  const [switchTime, setSwitchTime] = useState(5);
  const _switchTimeRef = useRef(5);
  const currentImageIdxRef = useRef(-1);
  const [colorHistory, setColorHistory] = useState([]);
  const timeoutIdRef = useRef(null);
  const hue = Math.floor(Math.random() * 360);
  const sat = Math.floor(Math.random() * 100);
  const lig = Math.floor(Math.random() * 100);
  const hslStr = `hsl(${hue}, ${sat}%, ${lig}%)`;
  const [props, api] = useSpring(() => {
    return {
      to: { background: hslStr },
      config: randomConfig(),
    };
  });

  const rgb = hslToRgb(hue, sat, lig);
  const fadeInOutProps = useMemo(() => {
    return {
      from: { opacity: 0 },
      to: async (next, cancel) => {
        await delay(300);
        await next({ opacity: 1 });
        await delay(5000);
        await next({ opacity: 0 });
      },
      config: config.gentle,
      reset: true
    };
  }, []);
  const [infoProps, infoApi] = useSpring(() => fadeInOutProps);

  const hslProps = { hsl: [hue, sat, lig] };
  const rgbProps = { rgb: [rgb.R, rgb.G, rgb.B] };
  const [hslSpring, hslApi] = useSpring(() => hslProps);
  const [rgbSpring, rgbApi] = useSpring(() => rgbProps);

  const changeBackground = useCallback((e, h = -1, s = -1, l = -1) => {
    const [ph, ps, pl] = hslApi.current[0].springs.hsl.animation.to;
    if (ph === h && ps === s && pl === l) {
      return;
    }
    setColorHistory(prev => {
      const newHistory = prev.concat([hslApi.current[0].springs.hsl.animation.to.map(v => Math.floor(v))]);
      if (newHistory.length > 100) {
        newHistory.shift();
      }
      return newHistory;
    });
    const newHue = h !== -1 ? h : Math.floor(Math.random() * 360);
    const newSat = s !== -1 ? s : Math.floor(Math.random() * 100);
    const newLig = l !== -1 ? l : Math.floor(Math.random() * 100);
    const newrgb = hslToRgb(newHue, newSat, newLig);

    api.start({
      to: { background: `hsl(${newHue}, ${newSat}%, ${newLig}%)` },
      delay: 100
    });
    infoApi.start(fadeInOutProps);
    hslApi.start({ hsl: [newHue, newSat, newLig], delay: 350 });
    rgbApi.start({ rgb: [newrgb.R, newrgb.G, newrgb.B], delay: 350 });
  }, [api, infoApi, hslApi, rgbApi, fadeInOutProps]);

  useEffect(() => {
    changeBackground(null);
  }, [changeBackground]);

  const onFullClicked = e => {
    e.stopPropagation();
    e.preventDefault();
    if (full) {
      document.exitFullscreen();
    } else {
      appRef.current.requestFullscreen();
    }
  };
  useEffect(() => {
    const onFullscreenError = () => {
      alert('お使いのブラウザは全画面表示に対応していないようです。');
    };
    document.addEventListener('fullscreenerror', onFullscreenError);
    const onFullscreenChange = () => {
      if (document.fullscreenElement) {
        setIsFull(true);
      } else {
        setIsFull(false);
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenerror', onFullscreenError);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  // 背景関連
  const updateImage = useCallback((delta = 1) => {
    const setT = () => {
      timeoutIdRef.current = setTimeout(() => {
        updateImage();
      }, _switchTimeRef.current * 1000);
    };
    if (images.length > 0) {
      let newIdx = (currentImageIdxRef.current + delta) % images.length;
      if (newIdx < 0) {
        newIdx += images.length;
      }
      if (currentImageIdxRef.current !== newIdx) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImageSrc(e.target.result);
          currentImageIdxRef.current = newIdx;
          setT();
        };
        reader.onerror = (e) => {
          // 失敗しても進めないとずっと失敗する
          currentImageIdxRef.current = newIdx;
          setT();
        };
        reader.onabort = reader.onerror;
        reader.readAsDataURL(images[newIdx]);
        return;
      }
    }
  }, [images, setImageSrc]);
  useEffect(() => {
    updateImage();
    return () => {
      clearTimeout(timeoutIdRef.current);
    };
  }, [updateImage]);
  const handlePrevClick = (e) => {
    if (images.length > 1) {
      e.preventDefault();
      e.stopPropagation();
      clearTimeout(timeoutIdRef.current);
      updateImage(-1);
    }
  };
  const handleNextClick = (e) => {
    if (images.length > 1) {
      e.preventDefault();
      e.stopPropagation();
      clearTimeout(timeoutIdRef.current);
      updateImage(1);
    }
  };

  // D&Dのイベントリスナ
  useEffect(() => {
    const app_elm = document.querySelector('.App');
    const preventDefaultAction = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };
    app_elm.addEventListener('dragenter', preventDefaultAction);
    app_elm.addEventListener('dragover', preventDefaultAction);
    const onDrop = (e) => {
      e.stopPropagation();
      e.preventDefault();
      const dt = e.dataTransfer;
      const files = dt.files;
      const newImages = handleFiles(files);
      clearTimeout(timeoutIdRef.current);
      setImages(images => {
        const newImagesArray = images.concat(newImages);
        doFisherYatesShuffle(newImagesArray);
        return newImagesArray;
      });
    };
    app_elm.addEventListener('drop', onDrop);
    return () => {
      app_elm.removeEventListener('dragenter', preventDefaultAction);
      app_elm.removeEventListener('dragover', preventDefaultAction);
      app_elm.removeEventListener('drop', onDrop);
    };
  }, []);
  // ボタンクリックによる背景の追加
  const handleAddImages = (e) => {
    const files = e.target.files;
    const newImages = handleFiles(files);
    clearTimeout(timeoutIdRef.current);
    setImages(images => {
      const newImagesArray = images.concat(newImages);
      doFisherYatesShuffle(newImagesArray);
      return newImagesArray;
    });
  };
  const handleResetImages = (e) => {
    setImages([]);
    setImageSrc(null);
    currentImageIdxRef.current = -1;
    clearTimeout(timeoutIdRef.current);
  };
  const handleSwitchTime = (e) => {
    let newSwitchTime = e.target.value;
    if (newSwitchTime < 1) newSwitchTime = 1;
    if (newSwitchTime > 3600) newSwitchTime = 3600;
    _switchTimeRef.current = newSwitchTime;
    setSwitchTime(newSwitchTime);
  };

  // スクロールを調整。appとセカンド画面と跨がないように修正する
  useEffect(() => {
    let tid = -1;
    const handleScroll = (e) => {
      if (tid > 0) {
        clearTimeout(tid);
      }
      if (enableAutoScroll === false) {
        return;
      }
      tid = setTimeout(() => {
        if (appRef.current == null) {
          tid = -1;
          return;
        }
        const secondRatio = window.scrollY / appRef.current.offsetHeight;
        if (secondRatio < 0.7) {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        } else if (secondRatio > 0.75 && secondRatio < 1) {
          window.scrollTo({ top: appRef.current.offsetHeight, left: 0, behavior: 'smooth' });
        } else {
          // do nothing
        }
        tid = -1;
      }, 250);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(tid);
    };
  }, [enableAutoScroll]);


  return (
    <>
      <animated.div ref={appRef} className="App" style={props} onClick={e => changeBackground(e)}>
        <BackgroundImage opacity={imgOpacity} imageSrc={imageSrc} isFitImage={isFitImage} imagePosition={imagePosition} />
        <animated.div className="Info" style={infoProps}>
          <animated.span style={{
            color: hslSpring.hsl.to((h, s, l) => l < 60 ? 'white' : 'black')
          }}>
            {hslSpring.hsl.to((h, s, l) => `hsl (${h.toFixed(0)}, ${s.toFixed(0)}%, ${l.toFixed(0)}%)`)}
          </animated.span>
          <animated.span style={{
            color: hslSpring.hsl.to((h, s, l) => l < 60 ? 'white' : 'black')
          }}>
            {rgbSpring.rgb.to((r, g, b) => `rgb (${r.toFixed(0)}, ${g.toFixed(0)}, ${b.toFixed(0)})`)}
          </animated.span>
          <animated.button style={{
            color: hslSpring.hsl.to((h, s, l) => l < 60 ? 'white' : 'black'),
            border: hslSpring.hsl.to((h, s, l) => `solid 5px ${l < 60 ? 'white' : 'black'}`)
          }} className="Info-btn" onClick={onFullClicked}>
            {full ? 'Exit' : 'Full'}
          </animated.button>
          <animated.button style={{
            opacity: images.length > 1 ? 1 : 0,
            color: hslSpring.hsl.to((h, s, l) => l < 60 ? 'white' : 'black'),
            border: hslSpring.hsl.to((h, s, l) => `solid 5px ${l < 60 ? 'white' : 'black'}`)
          }} className="Info-btn prev-btn" onClick={handlePrevClick}>
            &lt;&lt;
          </animated.button>
          <animated.button style={{
            opacity: images.length > 1 ? 1 : 0,
            color: hslSpring.hsl.to((h, s, l) => l < 60 ? 'white' : 'black'),
            border: hslSpring.hsl.to((h, s, l) => `solid 5px ${l < 60 ? 'white' : 'black'}`)
          }} className="Info-btn next-btn" onClick={handleNextClick}>
            &gt;&gt;
          </animated.button>
        </animated.div>
      </animated.div >
      <Second hslSpring={hslSpring} rgbSpring={rgbSpring} imgOpacity={imgOpacity}
        setImgOpacity={setImgOpacity} handleAddImages={handleAddImages}
        handleResetImages={handleResetImages} switchTime={switchTime}
        handleSwitchTime={handleSwitchTime} colorHistory={colorHistory}
        changeBackground={changeBackground} enableAutoScroll={enableAutoScroll}
        setAutoScroll={setAutoScroll} isFitImage={isFitImage}
        setFitImage={setFitImage} imagePosition={imagePosition}
        setImagePosition={setImagePosition}
      />
    </>
  );
}

/** props.imageSrcがあるときはimgタグになり、そうでないときはnullでいるコンポーネント */
function BackgroundImage(props) {
  let Img = null;
  if (props.imageSrc != null) {
    let imgClass = "bg-img";
    if (props.isFitImage === false) {
      imgClass = `${imgClass} position-${props.imagePosition}`;
    }
    Img = <img id="bgimage" style={{ opacity: props.opacity }} className={imgClass} alt="your specified" />;
  }
  useEffect(() => {
    const img = document.getElementById("bgimage");
    if (img == null) {
      return;
    }
    const container = img.parentElement;
    // イベントリスナを使用して、サイズを調整。引き伸ばして枠に合わせる。
    const justifyImg = (e) => {
      if (img == null || img.width === undefined) {
        return;
      }
      // 以前のサイズの読み取りを防ぐ
      img.style.width = "";
      img.style.height = "";
      // fitさせないなら初期化だけして終わる
      if (props.isFitImage === false) {
        return;
      }
      // 画像と空間の縦横それぞれのアスペクト比が必要
      const widthRatio = container.clientWidth / img.width;
      const heightRatio = container.clientHeight / img.height;
      // 片方だけ合わせる
      if (widthRatio < heightRatio) {
        img.style.width = `${img.width * widthRatio}px`;
      } else {
        img.style.height = `${img.height * heightRatio}px`;
      }
      // TODO: ここでタイマー開始する方が時間が保証される。
      img.removeEventListener("load", justifyImg);
    };
    img.addEventListener("load", justifyImg);
    let reqId = null;
    const debouncedJustify = (e) => {
      if (reqId != null) {
        window.cancelAnimationFrame(reqId);
      }
      reqId = window.requestAnimationFrame(() => {
        justifyImg();
        reqId = null;
      });
    };
    window.addEventListener("resize", debouncedJustify);

    if (props.imageSrc) {
      img.src = props.imageSrc;
    }
    return () => {
      img.removeEventListener("load", justifyImg);
      window.removeEventListener("resize", debouncedJustify);
    };
  }, [props.imageSrc, props.isFitImage]);

  return (
    <>
      {Img}
    </>
  );
}

function Second(props) {
  const hslSpring = props.hslSpring;
  const rgbSpring = props.rgbSpring;
  const colorElmList = [];
  for (let i = props.colorHistory.length; i > 0; i--) {
    const [h, s, l] = props.colorHistory[i - 1];
    const rgb = hslToRgb(h, s, l);
    colorElmList.push((
      <div className="color-item" key={i - 1}
        style={{
          backgroundColor: `hsl(${h}, ${s}%, ${l}%)`,
          color: `${l > 60 ? 'black' : 'white'}`
        }}
        onClick={(e) => props.changeBackground(e, h, s, l)}
      >
        <p>{`hsl (${h}, ${s} %, ${l} %)`}</p>
        <p>{`rgb (${rgb.R}, ${rgb.G} %, ${rgb.B} %)`}</p>
      </div >
    ));
  }
  // 背景画像の位置決め設定用divを生成
  const locatingDivs = [];
  for (let i = 1; i < 10; i++) {
    locatingDivs.push((
      <div className={i === props.imagePosition ? "locate-selection selected" : "locate-selection"}
        key={i} onClick={(e) => props.setImagePosition(i)}
      >
        <div className={`position-${i}`}></div>
      </div>
    ));
  }
  return (
    <div className="Second">
      <h2>カラー</h2>
      <div className="pref-item color-info">
        <animated.p>{hslSpring.hsl.to((h, s, l) => `hsl (${h.toFixed(0)}, ${s.toFixed(0)}%, ${l.toFixed(0)}%)`)}</animated.p>
        <animated.p>{rgbSpring.rgb.to((r, g, b) => `rgb (${r.toFixed(0)}, ${g.toFixed(0)}, ${b.toFixed(0)})`)}</animated.p>
      </div>
      <div className="pref-item color-history">
        {colorElmList}
      </div>
      <h2>オプション</h2>
      <div className="pref-item auto-scroll">
        <label htmlFor="auto-scroll-config">オートスクロール</label>
        <input type="checkbox" id="auto-scroll-config" checked={props.enableAutoScroll} onChange={(e) => props.setAutoScroll(p => !p)} />
      </div>
      <div className="pref-item img-opacity">
        <label htmlFor="img-opacity" data-tooltip="上の画面へドラッグ&ドロップ、または下のボタンから背景画像を設定できます">背景画像の不透明度</label>
        <input type="range" id="img-opacity" onChange={e => {
          let opcty = e.target.value;
          if (opcty < 0) opcty = 0;
          if (opcty > 1) opcty = 1;
          props.setImgOpacity(opcty);
        }}
          min="0" max="1" step="0.01" value={props.imgOpacity} />
      </div>
      <div className="pref-item specify-imgs">
        <input type="file" id="imgs-btn" multiple accept="image/*" className="visually-hidden" onChange={props.handleAddImages} />
        <label htmlFor="imgs-btn">背景画像</label>
        <button className="clear-btn" onClick={props.handleResetImages}>クリア</button>
      </div>
      <div className="pref-item img-switching">
        <label htmlFor="img-switch-time">背景画像の切り替え時間: {props.switchTime}秒</label>
        <input id="img-switch-time" type="range"
          min="1" max="3600" value={props.switchTime} onChange={props.handleSwitchTime} />
      </div>
      <div className="pref-item fit-image">
        <label htmlFor="fit-image-config">画像を全体表示</label>
        <input type="checkbox" id="fit-image-config" checked={props.isFitImage} onChange={(e) => props.setFitImage(p => !p)} />
      </div>
      {props.isFitImage === false ? <div>画像の表示位置の指定</div> : null}
      <div className="pref-item locate-image-wrapper" style={props.isFitImage ? { opacity: 0.3 } : { opacity: 1 }}>
        {locatingDivs}
      </div>
    </div>
  );
}

export default App;
